import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import homeFill from '@iconify/icons-eva/home-fill';
import checkmarkFill from '@iconify/icons-eva/checkmark-fill';
import { useSnackbar } from 'notistack';
// material
import { Box, Button, CircularProgress, Divider, MenuItem, Typography } from '@mui/material';
// components
import MenuPopover from '../../components/MenuPopover';
import ManagePropertiesDialog from './ManagePropertiesDialog';
// services
import webServices from '../../services/webServices';
import { throwIfRequestFailed } from '../../services';

// Multi-home switcher (card 194e).
//
// The backend half shipped first and deliberately: cards 194b/194c converted
// ~88 UID-keyed queries to resolve through the active home, because a switcher
// added before that would have shown Home B's page filled with Home A's
// belongings. That work is done, so switching is now real.
//
// Until this control existed a second property was invisible: `POST /address`
// has always been able to create a second address row, and the account that hit
// that had a home sitting in the database with no way to reach it.
//
// RENDERS NOTHING FOR A SINGLE-HOME ACCOUNT. `multiHomeEnabled` comes from the
// server for exactly this reason — a switcher on an account with one property
// is a control that cannot do anything, which is the overwhelming majority of
// accounts. It also means this component costs one request and no pixels for
// almost everyone.

export default function HomeSwitcher() {
  const anchorRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [homes, setHomes] = useState([]);
  const [activeHomeId, setActiveHomeId] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [managing, setManaging] = useState(false);
  // What the server says this caller may do at the home they are in (card
  // 194f). A co-homeowner or tenant must not be offered "Manage properties" —
  // the endpoints refuse them, and a button that only produces a 403 is worse
  // than no button.
  const [permissions, setPermissions] = useState(null);

  const load = useCallback(async () => {
    try {
      const resp = await webServices.getHomes();
      if (!resp || resp.success !== true) return;
      const data = resp.data || {};
      setHomes(data.homes || []);
      setActiveHomeId(data.activeHomeId || null);
      setEnabled(Boolean(data.multiHomeEnabled));
      setPermissions(data.permissions || null);
    } catch (err) {
      // Silent. This is chrome in the navbar on every page; a homes lookup
      // that fails must not put an error toast in front of someone who was
      // doing something else entirely.
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSwitch = async (homeId) => {
    if (homeId === activeHomeId || switching) {
      setOpen(false);
      return;
    }

    setSwitching(true);
    try {
      // The helpers resolve with the axios error instead of rejecting, so
      // without this a failed switch would reload the page and look like it
      // had worked — landing the user back on the home they started from with
      // no explanation.
      throwIfRequestFailed(await webServices.setActiveHome(homeId));

      // FULL RELOAD, deliberately.
      //
      // Switching home changes what almost every endpoint in the app returns,
      // and redux is holding items, tasks, warranties, budget and score for the
      // home we are leaving. Re-fetching "the important ones" is how Home B's
      // page ends up showing Home A's belongings — the precise bug the backend
      // scoping work existed to prevent. A reload is blunt, but this is a rare,
      // deliberate action and correctness is worth more here than smoothness.
      window.location.reload();
    } catch (err) {
      setSwitching(false);
      setOpen(false);
      enqueueSnackbar(err.message || 'Could not switch homes. Please try again.', { variant: 'error' });
    }
  };

  if (!enabled) return null;

  const activeHome = homes.find((home) => home.id === activeHomeId) || homes[0];

  return (
    <>
      <Button
        ref={anchorRef}
        onClick={() => setOpen(true)}
        color="inherit"
        startIcon={
          switching ? <CircularProgress size={16} color="inherit" /> : <Icon icon={homeFill} width={20} height={20} />
        }
        sx={{
          maxWidth: { xs: 160, sm: 280 },
          justifyContent: 'flex-start',
          color: 'text.primary',
          fontWeight: 'fontWeightMedium'
        }}
      >
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {activeHome ? activeHome.label : 'Your home'}
        </Box>
      </Button>

      <MenuPopover open={open} onClose={() => setOpen(false)} anchorEl={anchorRef.current} sx={{ width: 320 }}>
        <Box sx={{ py: 1.5, px: 2.5 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            Your properties
          </Typography>
        </Box>

        {homes.map((home) => {
          const isActive = home.id === activeHomeId;
          return (
            <MenuItem
              key={home.id}
              selected={isActive}
              disabled={switching}
              onClick={() => handleSwitch(home.id)}
              sx={{ py: 1, px: 2.5 }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  {home.label}
                </Typography>
                {/* Owner vs Resident matters here: the same person can own one
                    property and merely live at another, and those grant
                    different powers once they are switched into it. */}
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {home.role}
                </Typography>
              </Box>
              {isActive && <Icon icon={checkmarkFill} width={18} height={18} />}
            </MenuItem>
          );
        })}

        {/* The way back out. Until this existed, adding a property was a
            one-way door — there was no way to rename one, and no way to
            remove one at all. */}
        {permissions && permissions.isOwner && (
          <>
            <Divider sx={{ my: 1 }} />
            <MenuItem
              onClick={() => {
                setOpen(false);
                setManaging(true);
              }}
              sx={{ py: 1, px: 2.5 }}
            >
              <Typography variant="body2">Manage properties</Typography>
            </MenuItem>
          </>
        )}
      </MenuPopover>

      <ManagePropertiesDialog
        open={managing}
        onClose={() => setManaging(false)}
        homes={homes}
        activeHomeId={activeHomeId}
        onChanged={load}
      />
    </>
  );
}
